import firebase from "firebase/app";

import { useTranslation } from "react-i18next";

import generateCustomXMLFromTemplate from "astrid-distribution/src/actions/processors/metadata/customXml/generateCustomXMLFromTemplate";
import generateExcelFromTemplate from "astrid-distribution/src/actions/processors/metadata/excel/generateExcelFromTemplate";
import generateOnixFromTemplate from "astrid-distribution/src/actions/processors/metadata/onix/generateOnixFromTemplate";
import constructPipelineDocument from "astrid-distribution/src/api/constructPipelineDocument";

import metadataTemplateTypes from "../../../../superadmin/views/SuperAdminDistributionChannelView/constants/metadataTemplateTypes";

import getEnableDisableArticleChannelTexts from "./useEnableDisableArticleChannelTexts";

function downloadMetadata({ metadata, fileName, fileType }) {
	const blob = new Blob([metadata], { type: fileType });
	const link = document.createElement("a");
	link.download = fileName;
	link.href = window.URL.createObjectURL(blob);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

export default function useArticleDistributionChannelDropdownMenuOptions({ article, publisher, setDisableChannel }) {
	const { t } = useTranslation();

	return (channel) => {
		const enableDisableChannelText = getEnableDisableArticleChannelTexts({ t, channel, article });

		return [
			{
				text: enableDisableChannelText,
				action: () => {
					setDisableChannel(channel);
				},
			},
			{
				text: t("downloadZip", "Download ZIP"),
				hidden: !article.channels?.[channel.id]?.delivery?.zip?.url,
				action: async () => {
					const link = document.createElement("a");
					link.download = `${channel.name}.zip`;
					link.href = article.channels[channel.id].delivery.zip.url;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				},
			},
			{
				text: t("previewOnix", "Preview onix"),
				hidden: !channel.enabledTemplates?.includes?.(metadataTemplateTypes.ONIX),
				action: async () => {
					const document = await constructPipelineDocument(firebase, {
						article: { ref: article.ref },
						channel: { ref: channel?.ref },
						publisher: { ref: publisher?.ref },
					});
					const metadata = await generateOnixFromTemplate({ document });
					downloadMetadata({
						metadata,
						fileName: `onix-preview-${article.isbn}.xml`,
						fileType: "application/xml",
					});
				},
			},
			{
				text: t("previewExcel", "Preview excel"),
				hidden: !channel.enabledTemplates?.includes?.(metadataTemplateTypes.EXCEL),
				action: async () => {
					const document = await constructPipelineDocument(firebase, {
						article: { ref: article.ref },
						channel: { ref: channel?.ref },
						publisher: { ref: publisher?.ref },
					});
					const metadata = await generateExcelFromTemplate({
						document,
						domParser: (html) => {
							const parser = new DOMParser();
							return parser.parseFromString(html, "text/html");
						},
						type: "array",
					});

					downloadMetadata({
						metadata,
						fileName: `excel-preview-${article.isbn}.xlsx`,
						fileType: "application/octet-stream",
					});
				},
			},
			{
				text: t("previewCustomTemplate", "Preview custom metadata"),
				hidden: !channel.enabledTemplates?.includes?.(metadataTemplateTypes.CUSTOM_XML),
				action: async () => {
					const document = await constructPipelineDocument(firebase, {
						article: { ref: article.ref },
						channel: { ref: channel?.ref },
						publisher: { ref: publisher?.ref },
					});
					const metadata = await generateCustomXMLFromTemplate({ document });
					downloadMetadata({
						metadata,
						fileName: `custom-preview-${article.isbn}.xml`,
						fileType: "application/xml",
					});
				},
			},
		];
	};
}
